"use client";

import React from "react";
import { CircleCheck as CheckCircle2, PhoneCall, ShieldCheck, Clock, ArrowRight, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const highlights = [{
  text: "24/7 Emergency Dispatch"
}, {
  text: "Same-Day Clog Clearing"
}, {
  text: "Upfront, Honest Pricing"
}, {
  text: "Licensed & Insured Techs"
}];
export default function DrainHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="draincleaninghero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28">
      {/* Subtle decorative background glow */}
      <div className="absolute top-0 right-1/4 -z-10 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute bottom-0 left-10 -z-10 h-72 w-72 rounded-full bg-accent/10 blur-2xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Content Column */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="flex flex-col items-start lg:col-span-7">
            {/* Top Badge */}
            <div className="mb-4">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                <Clock className="size-3.5 text-primary" />
                <span>Jonesboro 24/7 Drain Specialists</span>
              </Badge>
            </div>

            {/* H1 Primary Heading */}
            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
              Fast & Reliable Drain Cleaning in Jonesboro, GA
            </h1>

            {/* Subheading */}
            <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Stubborn clogs, slow drains, or backed-up main sewer lines? Jonesboro Emergency Plumber delivers rapid, professional drain unclogging and hydro-jetting services with clear pricing and zero hassle.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto font-semibold shadow-md">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>

            {/* Trust Highlights Checklist */}
            <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 w-full pt-6 border-t border-border">
              {highlights.map((item, index) => <div key={item.text} data-index={index} className="flex items-center gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{item.text}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Image/Visual Card */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }} className="relative lg:col-span-5">
            <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-2xl bg-card border border-border shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_technician.png-wvc-sizes" alt="Plumbing technician inspecting and cleaning residential drains" loading="lazy" decoding="async" className="h-80 w-full object-cover sm:h-96" />

              {/* In-Card Floating Status Banner */}
              <div className="p-5 bg-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Wrench className="size-5" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-foreground">Advanced Equipment</h2>
                      <p className="text-xs text-muted-foreground">Snaking, Hydro-Jetting & Camera Inspection</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-secondary-foreground">
                    <ShieldCheck className="size-3.5 text-primary" />
                    <span>Guaranteed</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}